/*
 * EmptyState: what a list or section shows when there is nothing yet, with the next action.
 *   <EmptyState icon={<HardDrives />} title={t('servers:empty.title')} description={…}
 *     action={<Button asChild><Link to="/servers/new">…</Link></Button>} />
 * `compact` for inside panels and tables.
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  compact,
  className,
}: {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  secondaryAction?: ReactNode
  compact?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14 sm:py-20',
        className,
      )}
    >
      {icon ? (
        <span className="relative mb-2 grid size-14 place-items-center" aria-hidden="true">
          <span className="absolute inset-0 rounded-2xl bg-surface-tint ring-1 ring-hairline" />
          <span className="absolute -inset-3 -z-10 rounded-[28px] bg-[radial-gradient(closest-side,var(--color-brand-50),transparent)]" />
          <span className="relative text-brand-600 [&_svg]:size-6">{icon}</span>
        </span>
      ) : null}
      <h3 className={cn('font-semibold text-ink', compact ? 'text-15' : 'text-17')}>{title}</h3>
      {description ? <p className="max-w-md text-14 leading-6 text-muted">{description}</p> : null}
      {action || secondaryAction ? (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  )
}
